import { Flex, Heading, Section, Text } from '@radix-ui/themes';
import { BadgeCurrency } from 'components/common/badges/currency';
import { BadgeTrxType } from 'components/common/badges/transaction-type';
import { CommonDataList } from 'components/common/data-list';
import { EB } from 'components/layout/error-boundary';
import { CommonFlexColumn } from 'components/layout/flex-column';
import { PriceWhole } from 'lib/interfaces/database/price';
import { TransactionWhole } from 'lib/interfaces/database/transaction';
import { DateUtil, MONTH_FORMAT } from 'lib/utilities/date';
import { StringUtil } from 'lib/utilities/string';
import { ValueHelper } from 'lib/utilities/value-helper';
import { useEffect, useMemo, useState } from 'react';
import { PricesService } from 'services/prices';
import useCustodiansStore from 'stores/custodians';
import useInvestorsStore from 'stores/investors';

export const TransactionDetails = (props: { trx: TransactionWhole }) => {
  const { data: investors, load: loadInvestors } = useInvestorsStore();
  const { data: custodians, load: loadCustodians } = useCustodiansStore();

  const [prices, setPrices] = useState<PriceWhole[]>([]);

  const values = useMemo(
    () => ValueHelper.getTrxValues(props.trx, prices, new Date()),
    [props.trx, prices]
  );

  useEffect(() => {
    PricesService.getInstance()
      .search({ securityID: props.trx.securityID })
      .then((result) => {
        if (!result) {
          setPrices([]);
          return;
        }

        // ascending effective date
        result.sort((a, b) =>
          DateUtil.isBefore(a.effective, b.effective) ? -1 : 1
        );

        setPrices(result);
      });
  }, []);

  useEffect(() => {
    loadInvestors();
    loadCustodians();
  }, []);

  return (
    <EB>
      <CommonFlexColumn>
        <Section size="1">
          <Heading size="2" mb="4">
            Summary
          </Heading>

          <CommonDataList
            rows={[
              {
                label: 'Type',
                value: <BadgeTrxType value={props.trx.type} />,
              },
              {
                label: 'Security',
                value: props.trx._security?.name,
              },
              {
                label: 'Issuer',
                value: props.trx._security?._issuer?.name,
              },
              {
                label: 'Book Value',
                value: (
                  <Flex gap="1">
                    <Text>
                      {StringUtil.printMoney(
                        props.trx.amount,
                        0,
                        props.trx.currency
                      )}
                    </Text>
                    <BadgeCurrency value={props.trx.currency} />
                  </Flex>
                ),
              },
            ]}
          />
        </Section>

        <Section size="1">
          <Heading size="2" mb="4">
            Price History
          </Heading>

          <CommonDataList
            rows={prices.map((p) => ({
              label: DateUtil.printDate(p.effective),
              value: StringUtil.printMoney(p.pps, 2, props.trx.currency),
            }))}
          />
        </Section>

        <Section size="1">
          <Heading size="2" mb="4">
            Historical Value
          </Heading>

          <CommonDataList
            rows={values.map((p) => ({
              label: DateUtil.printDate(p.asOf, MONTH_FORMAT),
              value: StringUtil.printMoney(p.amount, 2, props.trx.currency),
            }))}
          />
        </Section>
      </CommonFlexColumn>
    </EB>
  );
};
